import { Box, Typography } from '@mui/material';
import { useEffect, useState } from 'react';
import { DocumentService } from '../Service/DocumentService';
import { useAppDispatch, useAppSelector } from '../store/hooks';
import { createNotification } from '../store/notifications/notification-slice';
import { Loading } from '../components/Loading';
import StrategyDocuments from '../components/StrategyDocuments/StrategyDocuments';

const Documents = () => {
  const user = useAppSelector(state => state.user.user);
  const [strategies, setStrategies] = useState<any[]>([]);
  const [additionalDocuments, setAdditionalDocuments] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const appDispatch = useAppDispatch();

  useEffect(() => {
    getStrategies();
  }, []);

  const handleDocumentDelete = async (documentId: string) => {
    try {
      await DocumentService.deleteDocument(documentId);

      const newStrategies = structuredClone(strategies);
      const requiredDocuments = [];

      for (const newStrategy of newStrategies) {
        requiredDocuments.push(...newStrategy.requiredDocuments);
      }

      const requiredDocument = requiredDocuments.find(
        (req: any) => req.document?.id === documentId
      );

      requiredDocument.document = null;
      setStrategies(newStrategies);

      appDispatch(
        createNotification({
          type: 'success',
          message: 'Document has been deleted!',
        })
      );
    } catch (error) {
      appDispatch(
        createNotification({
          type: 'error',
          message: 'Something went wrong, try again later!',
        })
      );
    }
  };

  const handleOtherDocumentDelete = async (documentId: string) => {
    try {
      await DocumentService.deleteDocument(documentId);

      const newAdditionalDocuments = structuredClone(additionalDocuments);

      const requiredDocument = newAdditionalDocuments.find(
        (req: any) => req.document?.id === documentId
      );
      requiredDocument.document = null;

      setAdditionalDocuments(newAdditionalDocuments);

      appDispatch(
        createNotification({
          type: 'success',
          message: 'Document has been deleted!',
        })
      );
    } catch (error) {
      appDispatch(
        createNotification({
          type: 'error',
          message: 'Something went wrong, try again later!',
        })
      );
    }
  };

  const handleFileUpload = async (file: any, requiredDocumentId: any) => {
    try {
      const result = await DocumentService.addDocument({
        id: requiredDocumentId,
        file,
      });

      const document: any = result.data;

      const newStrategies = structuredClone(strategies);
      const requiredDocuments = [];

      for (const newStrategy of newStrategies) {
        requiredDocuments.push(...newStrategy.requiredDocuments);
      }

      const requiredDocument = requiredDocuments.find(
        (req: any) => req.id === requiredDocumentId
      );

      requiredDocument.document = document;
      setStrategies(newStrategies);

      appDispatch(
        createNotification({
          type: 'success',
          message: document.title + ' successfully uploaded!',
        })
      );
    } catch (error) {
      appDispatch(
        createNotification({
          type: 'error',
          message: 'Something went wrong, try again later!',
        })
      );
    }
  };

  const handleOtherDocumentUpload = async (
    file: any,
    requiredDocumentId: any
  ) => {
    try {
      const result = await DocumentService.addDocument({
        id: requiredDocumentId,
        file,
      });

      const document: any = result.data;

      const newAdditionalDocuments = structuredClone(additionalDocuments);

      const requiredDocument = newAdditionalDocuments.find(
        (req: any) => req.id === requiredDocumentId
      );
      requiredDocument.document = document;

      setAdditionalDocuments(newAdditionalDocuments);

      appDispatch(
        createNotification({
          type: 'success',
          message: document.title + ' successfully uploaded!',
        })
      );
    } catch (error) {
      appDispatch(
        createNotification({
          type: 'error',
          message: 'Something went wrong, try again later!',
        })
      );
    }
  };
  const getStrategies = async () => {
    setIsLoading(true);
    try {
      const response = await Promise.all([
        DocumentService.getUserDocuments(),
        DocumentService.getAdditionalDocuments(),
      ]);

      const [{ data: allStrategies }, { data: allAdditionalDocuments }] =
        response;

      setStrategies(allStrategies);
      setAdditionalDocuments(allAdditionalDocuments);
    } catch (error) {
      appDispatch(
        createNotification({
          type: 'error',
          message: 'Something went wrong, try again later!',
        })
      );
      setIsLoading(false);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Box
      sx={{
        padding: '32px 36px',
      }}
    >
      <Typography
        sx={{
          color: '#091E42',
          fontSize: '20px',
          fontWeight: '600',
          lineHeight: '32px',
          marginBottom: '8px',
        }}
      >
        Documents
      </Typography>
      <Typography
        sx={{
          color: '#655C68',
          fontSize: '14px',
          fontWeight: '400',
          lineHeight: '24px',
          marginBottom: '24px',
        }}
      >
        You can find here the list of documents you need to upload for each
        applicable strategy
      </Typography>
      <Loading isLoading={isLoading}>
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: '24px',
          }}
        >
          {strategies.map(strategy => {
            return (
              <StrategyDocuments
                key={strategy.id}
                requiredDocuments={strategy.requiredDocuments}
                title={strategy.name}
                description={`Upload the following required documents for the ${strategy.name}
                strategy`}
                handleDocumentDelete={handleDocumentDelete}
                handleFileUpload={handleFileUpload}
              />
            );
          })}
          <StrategyDocuments
            requiredDocuments={additionalDocuments}
            title='Additional Documents'
            description='Upload all documents that don’t belong to a specific strategy'
            handleDocumentDelete={handleOtherDocumentDelete}
            handleFileUpload={handleOtherDocumentUpload}
          />
        </Box>
      </Loading>
    </Box>
  );
};

export default Documents;
